/**
 * FloorSelector — selector vertical de pisos.
 *
 * Se usa en SiteConfigView (modo edición de configuración) y en MapView Q4
 * (modo navegación). Comportamiento estándar Google/Apple Maps Indoor:
 * orden top-down (piso más alto arriba, subterráneo abajo), chip activo
 * resaltado, click cambia el piso activo.
 *
 * Si el sitio tiene UN solo piso (sitios mononivel como Latadia o EDS
 * Copec), el selector NO se renderiza — el padre puede asumir que el
 * único piso siempre está activo.
 *
 * Props:
 *   floors        — array de planos [{id, floor_order, floor_label, is_georeferenced}]
 *   activeFloorId — id del piso actualmente seleccionado
 *   onSelect      — (floorId) => void
 *   onAdd         — opcional: () => void para botón "+" Agregar piso
 *   onEditLabel   — opcional: (floor) => void para editar label/order
 *   position      — 'absolute' | 'static' (default absolute, sobre el plano)
 *   side          — 'right' | 'left' (default right)
 */

import { useState } from 'react'

export default function FloorSelector({
  floors = [],
  activeFloorId,
  onSelect,
  onAdd,
  onEditLabel,
  position = 'absolute',
  side = 'right',
}) {
  // Sitios mononivel no muestran selector
  if (floors.length <= 1 && !onAdd) return null

  // Orden top-down: piso 5, 4, 3, 2, 1, PB, S
  const sorted = [...floors].sort(
    (a, b) => (b.floor_order ?? 0) - (a.floor_order ?? 0),
  )

  const containerStyle = {
    position,
    top: position === 'absolute' ? 12 : undefined,
    [side]: position === 'absolute' ? 12 : undefined,
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    background: 'rgba(255,255,255,0.95)',
    borderRadius: 6,
    padding: 6,
    boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
    zIndex: 10,
    minWidth: 64,
  }

  return (
    <div style={containerStyle}>
      {sorted.map(floor => {
        const isActive = floor.id === activeFloorId
        const label = floor.floor_label
          || labelFromOrder(floor.floor_order)
        const ungeoref = !floor.is_georeferenced
        return (
          <button
            key={floor.id}
            onClick={() => onSelect && onSelect(floor.id)}
            onDoubleClick={() => onEditLabel && onEditLabel(floor)}
            title={
              `${label}${ungeoref ? ' — sin georreferenciar' : ''}`
              + (onEditLabel ? ' (doble click para editar)' : '')
            }
            style={{
              padding: '8px 10px',
              border: 'none',
              borderRadius: 4,
              background: isActive ? '#0091ff' : 'transparent',
              color: isActive ? 'white' : '#111',
              fontSize: 12,
              fontWeight: isActive ? 700 : 500,
              cursor: 'pointer',
              textAlign: 'center',
              position: 'relative',
              minHeight: 32,
            }}
          >
            {label}
            {ungeoref && (
              <span style={{
                position: 'absolute',
                top: 4, right: 4,
                width: 6, height: 6,
                borderRadius: '50%',
                background: '#f59e0b',
              }} aria-label="Sin georreferenciar" />
            )}
          </button>
        )
      })}
      {onAdd && (
        <button
          onClick={onAdd}
          title="Agregar piso"
          style={{
            padding: '6px 10px',
            border: '1px dashed #999',
            borderRadius: 4,
            background: 'transparent',
            color: '#333',
            fontSize: 14,
            fontWeight: 700,
            cursor: 'pointer',
            marginTop: 4,
          }}
        >
          +
        </button>
      )}
    </div>
  )
}


// ─── Helpers ──────────────────────────────────────────────────────────────

/**
 * Si el operador no ha setteado floor_label, generamos uno a partir del
 * floor_order. Convención chilena.
 */
function labelFromOrder(order) {
  if (order == null) return '—'
  if (order === 0) return 'PB'
  if (order < 0)   return order === -1 ? 'S' : `S${Math.abs(order)}`
  return `${order}`
}
